import React, { Component } from "react";
import clsx from "clsx";
import AppBar from "@material-ui/core/AppBar";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme, withStyles } from "@material-ui/core/styles";

import AllureDockerToolbar from "../../components/AllureDockerToolbar/AllureDockerToolbar";
import AllureDockerProjectsSideDrawer from "../../components/AllureDockerProjectsSideDrawer/AllureDockerProjectsSideDrawer";

import { DRAWER_WIDTH as drawerWidth } from "../../theme/tokens";

const styles = (theme) => ({
  root: {
    display: "flex",
  },
  appBar: {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  content: {
    flexGrow: 1,
    minWidth: 0,
    padding: theme.spacing(4),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  // Ключа drawerHeader в styles не было — распорка рендерилась как
  // class="undefined" с нулевой высотой, и первая строка страницы уезжала под
  // фиксированный AppBar. Это маскировал двойной паддинг вложенных <main>.
  drawerHeader: {
    ...theme.mixins.toolbar,
  },
});

/**
 * Единый источник истины о ширине экрана: решение принимается здесь и уходит
 * вниз пропом. Вторую независимую проверку внутри drawer заводить нельзя —
 * на один рендер они могут разойтись, и получится temporary-панель со
 * сдвинутым <main>.
 *
 * Не withWidth(): с дефолтными опциями он возвращает null на первом рендере и
 * мигнул бы пустой оболочкой. useMediaQuery с noSsr читает matchMedia прямо в
 * рендере, поэтому первый кадр уже верный. В jsdom matchMedia нет →
 * isNarrow === false → десктоп, что совпадает с прежней эвристикой
 * «неизвестность = широкий экран».
 */
const withNarrowViewport = (Wrapped) => {
  const WithNarrowViewport = (props) => {
    const theme = useTheme();
    // В MUI v4 breakpoints.down("sm") — это «ниже md», max-width: 959.95px.
    const isNarrow = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
    return <Wrapped {...props} isNarrow={isNarrow} />;
  };
  return WithNarrowViewport;
};

class AllureDockerBar extends Component {
  state = {
    // Дерево проектов — основная навигация, а не вспомогательная панель,
    // поэтому на десктопе открыто по умолчанию. На узком экране панель
    // накрывает контент (variant temporary), и открывать её сразу незачем.
    openSideDrawer: !this.props.isNarrow,
  };

  componentDidUpdate(prevProps) {
    if (prevProps.isNarrow === this.props.isNarrow) {
      return;
    }
    // Сузили окно с открытой панелью — иначе оверлей с подложкой накроет то,
    // что человек читал. Расширили — возвращаем навигацию, которую он не
    // закрывал сам.
    this.setState({ openSideDrawer: !this.props.isNarrow });
  }

  handleSideDrawerOpen = () => {
    this.setState({ openSideDrawer: true });
  };

  handleSideDrawerClose = () => {
    this.setState({ openSideDrawer: false });
  };

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.root}>
        <AppBar
          position="fixed"
          className={clsx(classes.appBar, {
            // На узком экране панель накрывает контент, а не раздвигает —
            // сдвигать AppBar не нужно.
            [classes.appBarShift]: !this.props.isNarrow && this.state.openSideDrawer,
          })}
        >
          <AllureDockerToolbar
            title="Allure Docker Service UI"
            isSideDrawerOpen={this.state.openSideDrawer}
            handleSideDrawerOpen={this.handleSideDrawerOpen}
            handleSideDrawerClose={this.handleSideDrawerClose}
            darkState={this.props.darkState}
            handleThemeChange={this.props.handleThemeChange}
            getProjects={this.props.getProjects}
            createFolder={this.props.createFolder}
            setAPIAlert={this.props.setAPIAlert}
            isLogoutNeeded={this.props.isLogoutNeeded}
            isSignInAnOption={this.props.isSignInAnOption}
          />
        </AppBar>

        <AllureDockerProjectsSideDrawer
          title="Allure Docker Service UI"
          projects={this.props.projects}
          folders={this.props.folders}
          isSideDrawerOpen={this.state.openSideDrawer}
          isNarrow={this.props.isNarrow}
          handleSideDrawerClose={this.handleSideDrawerClose}
          selectProject={this.props.selectProject}
          moveProject={this.props.moveProject}
          deleteFolder={this.props.deleteFolder}
          isMoving={this.props.isMoving}
          setAPIAlert={this.props.setAPIAlert}
        />

        <main
          className={clsx(classes.content, {
            // contentShift — это ровно marginLeft: 0. В узком режиме панель
            // накрывает контент, поэтому смещение под неё не нужно никогда.
            [classes.contentShift]: this.props.isNarrow || this.state.openSideDrawer,
          })}
        >
          <div className={classes.drawerHeader} />

          {this.props.children}
        </main>
      </div>
    );
  }
}

// withNarrowViewport внутри withStyles — useTheme() должен резолвиться под
// ThemeProvider.
export default withStyles(styles, { withTheme: true })(
  withNarrowViewport(AllureDockerBar)
);
